import { useMemo, useState } from "react";
import type { OutcomeRecord } from "@/data/impact";


/** Filters the documented outcome record by pillar and year. */
export function OutcomeFilter({ records }: { records: OutcomeRecord[] }) {
  const pillars = useMemo(
    () => ["All programmes", ...Array.from(new Set(records.map((r) => r.pillar)))],
    [records],
  );
  const years = useMemo(
    () => ["All years", ...Array.from(new Set(records.map((r) => r.year))).sort((a, b) => b.localeCompare(a))],
    [records],
  );

  const [pillar, setPillar] = useState(pillars[0]);
  const [year, setYear] = useState(years[0]);

  const visible = records.filter(
    (record) =>
      (pillar === "All programmes" || record.pillar === pillar) &&
      (year === "All years" || record.year === year),
  );

  const selectClass =
    "select-field mt-2 block w-full min-h-11 rounded-[12px] border border-line-strong bg-white px-3.5 py-2.5 text-ink transition-colors duration-200 hover:border-navy/40 focus-visible:border-navy";

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2 sm:max-w-xl">
        <div>
          <label htmlFor="filter-pillar" className="text-[0.9375rem] font-semibold text-ink">
            Filter by programme
          </label>
          <select
            id="filter-pillar"
            value={pillar}
            onChange={(event) => setPillar(event.target.value)}
            className={selectClass}
          >
            {pillars.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="filter-year" className="text-[0.9375rem] font-semibold text-ink">
            Filter by year
          </label>
          <select
            id="filter-year"
            value={year}
            onChange={(event) => setYear(event.target.value)}
            className={selectClass}
          >
            {years.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p aria-live="polite" className="mt-8 text-[0.9375rem] font-semibold text-ink">
        {visible.length} documented {visible.length === 1 ? "outcome" : "outcomes"}
      </p>

      {visible.length === 0 ? (
        <p className="mt-6 rounded-[14px] border border-line bg-white p-6 text-muted">
          No documented outcome matches that combination. Try widening the programme or year.
        </p>
      ) : (
        <ol className="mt-5 max-w-4xl space-y-4">
          {visible.map((record) => (
            <li key={record.id}>
              <article className="rounded-[20px] border border-line bg-white p-6 shadow-[0_1px_2px_rgba(12,29,51,0.04),0_12px_28px_-22px_rgba(12,29,51,0.22)] md:p-7">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[0.8125rem]">
                  <span className="rounded-full bg-mist px-2.5 py-1 font-semibold text-navy">
                    {record.pillar}
                  </span>
                  <span className="text-muted">{record.location}</span>
                  <span aria-hidden="true" className="text-line-strong">
                    ·
                  </span>
                  <span className="text-muted">{record.date ?? record.year}</span>
                </div>
                <h3 className="mt-4" style={{ fontSize: "var(--step-h4)" }}>
                  {record.headline}
                </h3>
                <p className="mt-2.5 max-w-2xl text-muted">{record.detail}</p>
              </article>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
